import { Award as AwardIcon } from "lucide-react";
import { AGENCY_PARTNERS, AWARD, BRANDS } from "@/data/siteContent";
import { GhostButton } from "@/components/brand/Bits";

function Tile({ item, dark = false, testId }) {
  const label = item.name;
  return (
    <div
      className={`card-lift group flex h-[86px] items-center justify-center rounded-xl border px-3 text-center sm:h-[100px] ${
        dark
          ? "border-white/15 bg-white/[0.05] hover:border-[#FFB25C]/70"
          : "border-[#E6E1D8] bg-white hover:border-[#F2811D]"
      }`}
      data-testid={testId}
    >
      {item.logo ? (
        <img
          src={item.logo}
          alt={label}
          loading="lazy"
          className="max-h-[46px] w-auto max-w-[86%] object-contain opacity-95 grayscale transition-[filter,opacity] duration-300 group-hover:opacity-100 group-hover:grayscale-0 sm:max-h-[54px]"
        />
      ) : (
        <span
          className={`font-heading text-[13px] font-extrabold leading-tight tracking-tight sm:text-sm ${
            dark ? "text-white/90" : "text-[#111111]"
          }`}
        >
          {label}
        </span>
      )}
    </div>
  );
}


export function BrandGrid() {
  return (
    <div data-testid="brands-section">
      <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
        {BRANDS.heading}
      </h2>
      <p className="mt-4 max-w-3xl text-base leading-relaxed body-text md:text-lg">
        {BRANDS.supporting}
      </p>
      {BRANDS.logosPending && (
        <p
          className="mt-4 inline-block rounded-full bg-[#F2EEE6] px-3 py-1 text-xs font-bold text-[#6B6459]"
          data-testid="brands-logos-pending"
        >
          Brand names shown as wordmarks — logo files pending
        </p>
      )}
      <ul
        className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
        data-testid="brand-grid"
      >
        {BRANDS.items.map((item) => (
          <li key={item.name}>
            <Tile
              item={item}
              testId={`brand-tile-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
          </li>
        ))}
      </ul>
      <p className="mt-6 max-w-3xl text-xs leading-relaxed body-text" data-testid="brands-disclaimer">
        {BRANDS.disclaimer}
      </p>
    </div>
  );
}

export function AgencyPartners() {
  return (
    <div className="mt-14 border-t border-[#E6E1D8] pt-10" data-testid="agency-partners-section">
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#8a8378]">
        {AGENCY_PARTNERS.heading}
      </p>
      <ul
        className="mt-6 flex flex-wrap items-center gap-x-10 gap-y-6"
        data-testid="agency-grid"
      >
        {AGENCY_PARTNERS.items.map((item) => (
          <li key={item.name} className="group">
            <img
              src={item.logo}
              alt={item.name}
              loading="lazy"
              data-testid={`agency-tile-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="h-7 w-auto max-w-[150px] object-contain opacity-60 grayscale transition-[filter,opacity] duration-300 group-hover:opacity-100 group-hover:grayscale-0 sm:h-8"
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AwardFeature() {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#E4B95C]/35 bg-[#141210] p-7 sm:p-10"
      data-testid="award-feature"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <span className="absolute -right-10 -top-16 h-56 w-56 rounded-full bg-[#E4B95C]/12 blur-[70px]" />
        <span className="absolute -bottom-20 left-10 h-56 w-56 rounded-full bg-[#F2811D]/14 blur-[70px]" />
      </div>
      <div className="relative">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4B95C]/45 bg-[#E4B95C]/10">
            <AwardIcon className="h-5 w-5 text-[#E4B95C]" aria-hidden />
          </span>
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#E4B95C]">
            {AWARD.award} · {AWARD.event}
          </span>
        </div>

        <h2 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          {AWARD.heading}
        </h2>
        <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/85 md:text-lg">
          {AWARD.copy}
        </p>

        <dl className="mt-8 grid gap-5 border-t border-white/12 pt-7 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Campaign", value: AWARD.campaign, testId: "award-campaign" },
            { label: "Award", value: AWARD.award, testId: "award-medal" },
            { label: "Category", value: AWARD.category, testId: "award-category" },
            {
              label: "Campaign year / award year",
              value: `${AWARD.campaignYear} / ${AWARD.awardYear}`,
              testId: "award-years",
            },
          ].map((row) => (
            <div key={row.label} data-testid={row.testId}>
              <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
                {row.label}
              </dt>
              <dd className="mt-1.5 font-heading text-base font-extrabold text-white">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-8">
          <GhostButton to="/contact" dark testId="award-view-campaign">
            Talk to us about this campaign
          </GhostButton>
        </div>
        <p className="mt-6 max-w-3xl text-xs leading-relaxed text-white/50" data-testid="award-note">
          {AWARD.note}
        </p>
      </div>
    </div>
  );
}

